import { useState,} from 'react';
import { 
  ResponsiveContainer 
} from 'recharts';

import { Filter, Settings, } from 'lucide-react';
import SettingDialog from './settings/SettingDialog';
import { BarChart } from './charts/BarChart';
import { FunnelChart } from './charts/FunnelChart';
import {StackedBarChart} from './charts/StackedBarChart';
import {PieChart} from './charts/PieChart';
import {ScatterChart} from './charts/ScatterChart';
import {LineChart} from './charts/LineChart';
import {GridView} from './charts/GridView';
import {SimpleTableView,TableView} from './charts/TableView';
import './MultiViewWidget.css';
import {useEffect} from 'react';
import MetricCard from './charts/MetricCard';
import { useHistory } from "react-router-dom";
import { useWebViewStore } from "../webview/useWebViewStore";
import {Link} from 'cdk-radial'
import {AIAnalyze} from './AIAnalyze';
import {MultiViewWidgetContainer,MultiViewWidgetHeader} from './styles';
import { useAnalytics } from '../../v2/third-party-integration/amplitude';
import { AMPLITUDE_EVENTS } from '../../constants';
import FilterDialog from './filters/FilterDialog';
import { useTranslation } from 'react-i18next';


const getErrorStatus = (error: any): number | undefined => {
  let status = error?.response?.status;
  if (typeof status !== 'number' && typeof error?.message === 'string') {
    const message = error.message;
    if (message.indexOf('401') !== -1) status = 401;
    else if (message.indexOf('403') !== -1) status = 403;
    else if (message.indexOf('404') !== -1) status = 404;
    else if (message.indexOf('429') !== -1) status = 429;
    else if (message.indexOf('500') !== -1) status = 500;
  }
  return status;
};

const getErrorMessageKey = (error: any): string => {
  const status = getErrorStatus(error);
  if (status === 429) return 'dashboard.errors.rateLimit';
  if (typeof status === 'number' && status >= 500) return 'dashboard.errors.serverError';
  if (typeof status === 'number' && status >= 400) return 'dashboard.errors.configRequired';
  return 'dashboard.errors.default';
};

const MultiViewWidget = ({
  data, 
  widget,
  filter,
  error,
  filterComponent,
  onSettingSave
}:any) => {

  const {title,dataKey,defaultView,view, link, id, fields, height, isFilterAvailable} = widget;

  const noPermission = widget?.hasPermission === false;

  const [currentView, setCurrentView] = useState() as any;
  const [showSettings, setShowSettings] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const history = useHistory();
  const { setCustomWebView } = useWebViewStore();
  const { trackEvent } = useAnalytics();
  const { t } = useTranslation();

  useEffect(() => {
    setCurrentView(view || defaultView);
  }, [view,defaultView]);

  useEffect(() => {
    if (noPermission) {
      trackEvent(AMPLITUDE_EVENTS.WIDGET_PERMISSION_DENIED, { 'Widget': title });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noPermission, id]);

  useEffect(() => {
    if (!noPermission && error) {
      trackEvent(AMPLITUDE_EVENTS.DASHBOARD_API_ERROR, {
        'Dashboard': title,
        'Error Type': getErrorStatus(error) ?? 'unknown',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, noPermission, title]);

  const handleSettingSave= (fields:any, filter:any) =>{
    onSettingSave(fields,filter)
    setShowSettings(false);
  }

  const handleFilterSave= (fields:any, filter:any) =>{
    onSettingSave(fields,filter)
    setShowFilter(false);
  }

  const launchApp = () => {
    trackEvent(AMPLITUDE_EVENTS.SELECT_DASHBOARD_LINK, {
      'Item': title
    });
    history.push(`/unify/view/${id}/cta`);
    setCustomWebView({ id: id, url: link });
  } 

  const styles = {
    settingsBtn: {
      padding: '1px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    filterBtn: {
      marginLeft:'10px',
      padding: '1px',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  }
  const renderChart = () => {
    const commonProps = {
      data,
    };
    switch (currentView) {
      case 'metrics':
        return (
          <MetricCard commonProps={commonProps} data={data} widget={widget} />
        )
      case 'line':
        return (
          <LineChart commonProps={commonProps} data={data} widget={widget}  />
        );
      case 'stacked-bar':
        return (
          <StackedBarChart commonProps={commonProps} data={data} widget={widget} />
        );
      case 'bar':
        return (
          <BarChart commonProps={commonProps} data={data} widget={widget} responsive />
        );
      case 'pie':
        return (
          <PieChart commonProps={commonProps} data={data} widget={widget} />
        );
      case 'scatter':
        return (
          <ScatterChart commonProps={commonProps} data={data} widget={widget}/>
        );
      case 'funnel':
        return (
          <FunnelChart commonProps={commonProps} data={data} widget={widget}/>
        );

      case 'table':
        const isMulti = Array.isArray(dataKey)
        return isMulti ? renderTableView() : renderSimpleTableView();

      case 'grid':
        return (<GridView data={data} widget={widget} > </GridView>);

      default:
        return null;
    }
  };
  
  const renderSimpleTableView = () => {
    return (
      <SimpleTableView data={data} widget={widget}> </SimpleTableView>
    );
  };

  const renderTableView = () => {
    return (
      <TableView data={data} widget={widget} ></TableView>
    );
  };

  const operatorMap: Record<string, string> = {
  gt: "greater than",
  gte: "greater than or equal to",
  lt: "less than",
  lte: "less than or equal to",
  eq: "equal to"
};

  return (
    <MultiViewWidgetContainer >
      <MultiViewWidgetHeader>
        <div style={{display:'flex',justifyContent:'space-between',width:'100%'}}>
          {link && !error && !noPermission &&
            <div ><Link isInline text={title} onClick={launchApp}  style={{ cursor: 'pointer', fontSize:'14px', fontWeight:'500' }}/> </div>
          }
          {(!link || error || noPermission) &&
            <div ><h4 className="widget-title">{title}</h4></div>
          }
          {!noPermission &&
          <div style={{display:'flex', alignItems:'center', height:'fit-content'}}>
            {!error && <AIAnalyze data={data} fields={fields} operatorMap={operatorMap} dashboard={title}></AIAnalyze>}
                <div style={styles.settingsBtn}
                  onClick={() => setShowSettings(true)}
                  title="Threshold Settings">
                    <Settings size={16} color="#64748b" />
                </div>
              { isFilterAvailable && <div style={styles.filterBtn}
                  onClick={() => setShowFilter(true)}
                  title="Filter Settings">
                    <Filter size={16} color="#64748b" />
                </div>
                }
          </div>
          }
        </div>
        
      </MultiViewWidgetHeader>
      
      {noPermission && <div style={{ padding: '10px' }}>
        {t('dashboard.insufficientPermissions', 'This chart is unavailable as you do not have required permissions.')}
        </div>}

      {!noPermission && error && <div style={{ padding: '10px' }}>
        {t(getErrorMessageKey(error))}
        </div>}

      {!noPermission && !error &&
        <div className="widget-content" >
          {['line', 'stacked-bar', 'bar', 'area', 'pie', 'scatter'].includes(currentView) ? (
            <div style={{ 
              height: height || '200px', 
              width: '100%',
              margin: '0 auto', 
            }}>
              <ResponsiveContainer width="100%" height="100%">
                {renderChart()}
              </ResponsiveContainer>
            </div>
          ) : (
            renderChart()
          )}
        </div>
      }

        <SettingDialog
            isOpen={showSettings}  
            widget={widget} 
            onSave={handleSettingSave} 
            filterComponent={filterComponent}
            onClose={() => setShowSettings(false)}
    />

      <FilterDialog
            filter={filter}
            isOpen={showFilter}  
            widget={widget} 
            onSave={handleFilterSave} 
            filterComponent={filterComponent}
            onClose={() => setShowFilter(false)}
    />


    </MultiViewWidgetContainer>
  );
};

export default MultiViewWidget;
